import { createContext, useContext, useMemo, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import { GraphQL } from '@guestgoodie/graphql'

import { useBranding } from '@/features/branding'
import { localesAsOptions } from '@/features/translations'
import Dropdown from '@/components/ui/Dropdown'

type ContextProps<T extends FieldValues = FieldValues> = {
  hasTranslations: boolean
  isTranslatable: (name: string) => boolean
  fields: (keyof T)[]
  locale: GraphQL.AppLocale
  locales: GraphQL.AppLocale[]
  defaultLocale: GraphQL.AppLocale
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Context = createContext<ContextProps<any> | null>(null)

type Props = {
  translatableFields: string[]
}

export function Translation(props: React.PropsWithChildren<Props>) {
  const { children, translatableFields } = props

  const {
    branding: { defaultLocale, supportedLocales }
  } = useBranding()

  const [currentLocale, setCurrentLocale] = useState(defaultLocale)

  const locales = useMemo(
    () => Array.from(new Set([defaultLocale, ...(supportedLocales ?? [])])),
    [defaultLocale, supportedLocales]
  )

  const value = useMemo(
    () => ({
      isTranslatable: (name: string) => {
        const sanitizedName = name.replace(/\.[0-9]+\./, '.[].')

        return translatableFields.includes(sanitizedName)
      },
      fields: translatableFields,
      locale: currentLocale,
      locales,
      defaultLocale,
      hasTranslations: translatableFields.length > 0
    }),
    [translatableFields, currentLocale, defaultLocale, locales]
  )

  const options = useMemo(() => {
    return localesAsOptions(locales)
  }, [locales])

  return (
    <Context value={value}>
      {!!translatableFields && locales.length > 1 && (
        <div className="flex flex-row items-center justify-end p-2.5">
          <div>
            <Dropdown
              value={currentLocale}
              onChange={setCurrentLocale}
              options={options}
            />
          </div>
        </div>
      )}
      {children}
    </Context>
  )
}

export function useTranslation<T extends FieldValues>() {
  const context = useContext(Context)

  if (!context) return null
  return context as ContextProps<T>
}
